import { useMsal } from '@azure/msal-react'
import { useMutation } from '@tanstack/react-query'
import { Building2, Loader2, RefreshCw } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useBrandingStore } from '../stores/branding'
import { useAuthStore } from '../stores/auth'

interface ProvisionTenantPageProps {
  // Passed down from App.tsx's single useCurrentUser() call site, same
  // reason as AccessBlockedPage's onRetry prop: a second observer on the
  // same query key refetches on every mount (staleTime defaults to 0),
  // which would re-run a second copy of the store-syncing effect. See
  // AuthenticatedApp's comment.
  onProvisioned: () => void
}

// Reachable only when authStatus === 'not_provisioned' -- App.tsx's
// allowlist guard, which itself is only ever set from a
// TENANT_NOT_PROVISIONED authMe rejection (useCurrentUser.ts). Once
// provisioning succeeds, onProvisioned() re-runs authMe; the tenant's
// schema now exists, withPooledUserAuth.ts's existing first-user bootstrap
// creates the admin row, and authStatus moves to 'authenticated' -- which
// structurally unmounts this whole component in favor of AppShell. There is
// no state in which this page is still mounted and the tenant is already
// provisioned, so the button needs no "already provisioned" flag of its own.
export default function ProvisionTenantPage({ onProvisioned }: ProvisionTenantPageProps) {
  const { instance } = useMsal()
  const { name } = useBrandingStore()
  const { provisioningInfo } = useAuthStore()

  const canProvision = provisioningInfo?.canProvision ?? false
  const tenantSlug = provisioningInfo?.tenantSlug ?? null

  const provision = useMutation({
    mutationFn: () => {
      if (!tenantSlug) throw new Error('Missing tenant slug -- cannot provision')
      return api.post('/admin/tenant/migrate', { tenantSlug })
    },
    onSuccess: () => onProvisioned(),
  })

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-6 text-center">
        <div className="flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Building2 className="h-8 w-8 text-primary" />
          </div>
        </div>

        {canProvision ? (
          <>
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold text-foreground">Set up {name}</h1>
              <p className="text-sm text-muted-foreground">
                Your organization hasn't been set up in {name} yet. Click below to set
                up your organization's workspace — this only needs to happen once.
              </p>
            </div>

            {provision.isError && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-left">
                <p className="text-sm text-destructive">
                  {provision.error instanceof ApiError
                    ? provision.error.message
                    : 'Something went wrong while setting up your workspace.'}
                </p>
              </div>
            )}

            <button
              onClick={() => provision.mutate()}
              disabled={provision.isPending}
              className="mx-auto flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {provision.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Setting up…
                </>
              ) : provision.isError ? (
                <>
                  <RefreshCw className="h-4 w-4" /> Retry
                </>
              ) : (
                `Provision ${name}`
              )}
            </button>
          </>
        ) : (
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-foreground">Almost there</h1>
            <p className="text-sm text-muted-foreground">
              {provisioningInfo?.message ??
                `${name} hasn't finished setting up for your organization yet. Contact your administrator.`}
            </p>
          </div>
        )}

        <button
          onClick={() => instance.logoutRedirect()}
          className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
        >
          Sign out
        </button>
      </div>
    </div>
  )
}
